// react
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate, useParams } from '@tanstack/react-router';

// material ui icons
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';

// material ui
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

// api
import {
  useDeleteEnergyGroupMutation,
  useGetEnergyGroupQuery,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

const DeleteEnergyGroupDialog = ({ open, onClose }) => {
  const { t } = useTranslation('energyxplorer');

  const { energyGroupId } = useParams({ strict: false });

  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    {
      skip: !energyGroupId,
    },
  );

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const [deleteEnergyGroup] = useDeleteEnergyGroupMutation();

  const handleDeleteEnergyGroup = async (energyGroupId) => {
    const result = await deleteEnergyGroup({
      groupId: energyGroupId,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({ message: 'Gruppe slettet', severity: 'success' }),
      );
      navigate({ to: `/$accountId/energyxplorer` });
      onClose(false);
    }
    // Errors are handled globally by apiErrorMiddleware (snackbar + App Insights).
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('energy_group_dialogs.delete.title')}</DialogTitle>
      <DialogContent dividers>
        <Typography>
          {t('energy_group_dialogs.delete.message')} {selectedEnergyGroup?.name}
          {t('energy_group_dialogs.delete.warning')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="text" sx={{ textTransform: 'none' }} onClick={onClose}>
          {t('energy_group_dialogs.delete.actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            if (selectedEnergyGroup) {
              handleDeleteEnergyGroup(selectedEnergyGroup.energyGroupId);
            }
            onClose();
          }}
          startIcon={<DeleteRoundedIcon />}
          color="error"
        >
          {t('energy_group_dialogs.delete.actions.remove')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteEnergyGroupDialog;
